// cookieconsent-config.js

// Common constants for cookieconsent
import * as CONSTANTS from "./cookieconsent.common.js";
// Import the CookieConsent library and its styles
import * as CookieConsent from "vanilla-cookieconsent";
//import "vanilla-cookieconsent/dist/cookieconsent.css";

// Import the helper functions
import initBodyResize from "./cookieconsent.helpers.js";
initBodyResize(CookieConsent);

// Define dataLayer and the gtag function.
window.dataLayer = window.dataLayer || [];
function gtag() {
    dataLayer.push(arguments);
}

/**
 * Update gtag consent according to the users choices made in CookieConsent UI
 */
function updateGtagConsent() {
    gtag("consent", "update", {
        [CONSTANTS.SERVICE_ANALYTICS_STORAGE]: CookieConsent.acceptedService(CONSTANTS.SERVICE_ANALYTICS_STORAGE, CONSTANTS.CAT_ANALYTICS) ? "granted" : "denied",
        [CONSTANTS.SERVICE_AD_STORAGE]: CookieConsent.acceptedService(CONSTANTS.SERVICE_AD_STORAGE, CONSTANTS.CAT_ADVERTISEMENT) ? "granted" : "denied",
        [CONSTANTS.SERVICE_AD_USER_DATA]: CookieConsent.acceptedService(CONSTANTS.SERVICE_AD_USER_DATA, CONSTANTS.CAT_ADVERTISEMENT) ? "granted" : "denied",
        [CONSTANTS.SERVICE_AD_PERSONALIZATION]: CookieConsent.acceptedService(CONSTANTS.SERVICE_AD_PERSONALIZATION, CONSTANTS.CAT_ADVERTISEMENT) ? "granted" : "denied",
        [CONSTANTS.SERVICE_FUNCTIONALITY_STORAGE]: CookieConsent.acceptedService(CONSTANTS.SERVICE_FUNCTIONALITY_STORAGE, CONSTANTS.CAT_FUNCTIONALITY) ? "granted" : "denied",
        [CONSTANTS.SERVICE_PERSONALIZATION_STORAGE]: CookieConsent.acceptedService(CONSTANTS.SERVICE_PERSONALIZATION_STORAGE, CONSTANTS.CAT_FUNCTIONALITY) ? "granted" : "denied",
        [CONSTANTS.SERVICE_SECURITY_STORAGE]: CookieConsent.acceptedService(CONSTANTS.SERVICE_SECURITY_STORAGE, CONSTANTS.CAT_SECURITY) ? "granted" : "denied",
    });
}

CookieConsent.run({
    // See: https://cookieconsent.orestbida.com/reference/configuration-reference.html#guioptions
    // ...
    guiOptions: {
        consentModal: {
            //layout: "bar inline",
            //position: "bottom",
            equalWeightButtons: false,
        },
    },

    // Trigger consent update when user choices change
    onFirstConsent: () => {
        updateGtagConsent();
    },
    onConsent: () => {
        updateGtagConsent();
    },
    onChange: () => {
        updateGtagConsent();
    },

    // Configure categories and services
    categories: {
        [CONSTANTS.CAT_NECESSARY]: {
            enabled: true, // this category is enabled by default
            readOnly: true, // this category cannot be disabled
        },
        [CONSTANTS.CAT_ANALYTICS]: {
            autoClear: {
                cookies: [
                    {
                        name: /^_ga/, // regex: match all cookies starting with '_ga'
                    },
                    {
                        name: "_gid", // string: exact cookie name
                    },
                ],
            },
            // See: https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
            services: {
                [CONSTANTS.SERVICE_ANALYTICS_STORAGE]: {
                    label: "Enables storage (such as cookies) related to analytics e.g. visit duration.",
                },
            },
        },
        [CONSTANTS.CAT_ADVERTISEMENT]: {
            services: {
                [CONSTANTS.SERVICE_AD_STORAGE]: {
                    label: "Enables storage (such as cookies) related to advertising.",
                },
                [CONSTANTS.SERVICE_AD_USER_DATA]: {
                    label: "Sets consent for sending user data related to advertising to Google.",
                },
                [CONSTANTS.SERVICE_AD_PERSONALIZATION]: {
                    label: "Sets consent for personalized advertising.",
                },
            },
        },
        [CONSTANTS.CAT_FUNCTIONALITY]: {
            services: {
                [CONSTANTS.SERVICE_FUNCTIONALITY_STORAGE]: {
                    label: "Enables storage that supports the functionality of the website or app e.g. language settings.",
                },
                [CONSTANTS.SERVICE_PERSONALIZATION_STORAGE]: {
                    label: "Enables storage related to personalization e.g. video recommendations.",
                },
            },
        },
        [CONSTANTS.CAT_SECURITY]: {
            services: {
                [CONSTANTS.SERVICE_SECURITY_STORAGE]: {
                    label: "Enables storage related to security such as authentication functionality, fraud prevention, and other user protection.",
                },
            },
        },
    },

    language: {
        default: "en",
        translations: {
            en: {
                // See: https://support.google.com/tagmanager/answer/10718549?hl=en
                consentModal: {
                    title: "We use cookies",
                    description: "This website uses essential cookies to ensure its proper operation and tracking cookies to understand how you interact with it. The latter will be set only after consent.",
                    acceptAllBtn: "Accept all",
                    acceptNecessaryBtn: "Reject all",
                    showPreferencesBtn: "Manage preferences",
                },
                preferencesModal: {
                    title: "Manage cookie preferences",
                    acceptAllBtn: "Accept all",
                    acceptNecessaryBtn: "Reject all",
                    savePreferencesBtn: "Accept current selection",
                    closeIconLabel: "Close modal",
                    sections: [
                        {
                            title: "Cookie usage",
                            description: "We use cookies to ensure the basic functionalities of the website and to enhance your online experience.",
                        },
                        {
                            title: "Strictly necessary cookies",
                            description: "These cookies are essential for the proper functioning of the website, for example for user authentication.",
                            linkedCategory: CONSTANTS.CAT_NECESSARY,
                            cookieTable: {
                                headers: {
                                    name: "Name",
                                    domain: "Service",
                                    description: "Description",
                                    expiration: "Expiration",
                                },
                                body: [
                                    {
                                        name: "cc_cookie",
                                        domain: location.hostname,
                                        description: "A cookie that allows us to remember your cookie preferences",
                                        expiration: "6 months",
                                    },
                                ],
                            },
                        },
                        {
                            title: "Analytics",
                            description: "Cookies used for analytics help collect data that allows services to understand how users interact with a particular service. These insights allow services both to improve content and to build better features that improve the user’s experience.",
                            linkedCategory: CONSTANTS.CAT_ANALYTICS,
                            cookieTable: {
                                headers: {
                                    name: "Name",
                                    domain: "Service",
                                    description: "Description",
                                    expiration: "Expiration",
                                },
                                body: [
                                    {
                                        name: "_ga",
                                        domain: "Google Analytics",
                                        description: 'Cookie set by <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: "Expires after 12 days",
                                    },
                                    {
                                        name: "_gid",
                                        domain: "Google Analytics",
                                        description: 'Cookie set by <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: "Session",
                                    },
                                ],
                            },
                        },
                        {
                            title: "Advertising",
                            description: 'Google uses cookies for advertising, including serving and rendering ads, personalizing ads (depending on your ad settings at <a href="https://g.co/adsettings">g.co/adsettings</a>), limiting the number of times an ad is shown to a user, muting ads you have chosen to stop seeing, and measuring the effectiveness of ads.',
                            linkedCategory: CONSTANTS.CAT_ADVERTISEMENT,
                        },
                        {
                            title: "Functionality",
                            description: "Cookies used for functionality allow users to interact with a service or site to access features that are fundamental to that service. Things considered fundamental to the service include preferences like the user’s choice of language, product optimizations that help maintain and improve a service, and maintaining information relating to a user’s session, such as the content of a shopping cart.",
                            linkedCategory: CONSTANTS.CAT_FUNCTIONALITY,
                        },
                        {
                            title: "Security",
                            description: "Cookies used for security authenticate users, prevent fraud, and protect users as they interact with a service.",
                            linkedCategory: CONSTANTS.CAT_SECURITY,
                        },
                        {
                            title: "More information",
                            description: 'For any queries in relation to the policy on cookies and your choices, please <a href="/contact-us/">contact us</a>.',
                        },
                    ],
                },
            },
        },
    },
});
